/**
 * challengeTransform.helper
 * Maps raw API challenge response shape → typed ChallengeWithStats.
 * Separated from challengesApi.ts for file-size compliance.
 */

import type { ChallengeWithStats, SealedRequiredMarket } from '../../../types/challenges';
import { normalizeDisplayAvatarId } from '../../avatars/types/avatar.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

/** Raw participant shape from the API (before mapping). */
export interface RawParticipant {
  userId?: string;
  user?: { id?: string; email?: string; avatar?: string; avatarId?: string; avatarConfig?: EquippedMap | null; firstName?: string; lastName?: string; username?: string };
  email?: string;
  avatar?: string;
  avatarId?: string;
  avatarConfig?: EquippedMap | null;
  firstName?: string;
  lastName?: string;
  username?: string;
  status?: string;
}

/** Raw pick shape from the API (before mapping). */
export interface RawPick {
  id: string;
  challengeId?: string;
  userId?: string;
  user?: { id?: string };
  matchId?: string;
  match?: { id?: string };
  marketId?: number | string;
  marketName?: string;
  value?: string;
  odd?: number | string;
  pointsEarned?: number;
  isCorrectOutcome?: boolean;
  isExactScore?: boolean;
}

/** Raw challenge shape returned by the API (before mapping). */
export interface RawApiChallenge {
  id: string;
  creatorId?: string;
  name?: string;
  description?: string;
  oddsSnapshot?: unknown;
  stakeCoins?: number;
  status?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  participantIds?: string[];
  groupId?: string;
  groupName?: string;
  matchIds?: string[];
  requiredMarkets?: Record<string, SealedRequiredMarket[]>;
  exactScorePoints?: number;
  correctOutcomePoints?: number;
  league?: string;
  startDate?: string;
  endDate?: string;
  picks?: RawPick[];
  participants?: RawParticipant[];
  participantCount?: number;
  matchCount?: number;
  isOpen?: boolean;
  takenAt?: string | null;
  leader?: { userId?: string; userName: string };
  myRank?: number;
  myPoints?: number;
  participantStatuses?: Array<{ userId: string; status: string }>;
  participantDisplayNamesById?: Record<string, string>;
  participantAvatarsById?: Record<string, string>;
  participantAvatarIdsById?: Record<string, string>;
  participantAvatarConfigsById?: Record<string, EquippedMap | null>;
}

/** Transform one challenge from API shape to ChallengeWithStats (shared by list and page fetches). */
export function mapChallengeFromApi(challenge: RawApiChallenge): ChallengeWithStats & {
  participantEmailsById: Record<string, string>;
  participantDisplayNamesById: Record<string, string>;
  participantAvatarsById: Record<string, string>;
  participantAvatarIdsById: Record<string, string>;
  participantAvatarConfigsById: Record<string, EquippedMap | null>;
} {
  const participantIds: string[] = [];
  if (challenge.participantIds && Array.isArray(challenge.participantIds)) {
    participantIds.push(...challenge.participantIds);
  }

  const normalizeId = (id: string) => String(id).trim().toLowerCase();
  const seenNormalized = new Set(participantIds.map(normalizeId));
  const participantEmailsById: Record<string, string> = {};
  let participantDisplayNamesById: Record<string, string> = challenge.participantDisplayNamesById ?? {};
  let participantAvatarsById: Record<string, string> = challenge.participantAvatarsById ?? {};
  let participantAvatarIdsById: Record<string, string> = challenge.participantAvatarIdsById ?? {};
  let participantAvatarConfigsById: Record<string, EquippedMap | null> = challenge.participantAvatarConfigsById ?? {};

  if (challenge.participants && Array.isArray(challenge.participants)) {
    const builtAvatars: Record<string, string> = {};
    const builtAvatarIds: Record<string, string> = {};
    const builtAvatarConfigs: Record<string, EquippedMap | null> = {};
    challenge.participants.forEach((p: RawParticipant) => {
      const userId = p.userId || p.user?.id;
      const email = p.email || p.user?.email;
      const avatar = p.user?.avatar ?? p.avatar;
      const avatarId = p.user?.avatarId ?? p.avatarId;
      const avatarConfig = p.user?.avatarConfig ?? p.avatarConfig;
      const firstName = p.user?.firstName ?? p.firstName;
      const lastName = p.user?.lastName ?? p.lastName;
      const username = p.user?.username ?? p.username;

      if (userId) {
        const norm = normalizeId(userId);
        if (!seenNormalized.has(norm)) { seenNormalized.add(norm); participantIds.push(userId); }
      }
      const avatarStr = avatar != null && String(avatar).trim() !== '' ? String(avatar) : null;
      const avatarIdStr = avatarId != null && String(avatarId).trim() !== '' ? String(avatarId) : null;
      if (userId && avatarStr) builtAvatars[userId] = avatarStr;
      if (userId) builtAvatarIds[userId] = normalizeDisplayAvatarId(avatarIdStr);
      if (userId && avatarConfig != null) builtAvatarConfigs[userId] = avatarConfig;
      if (userId && email && !email.includes('@placeholder.com')) participantEmailsById[userId] = email;

      const displayName =
        [firstName, lastName].filter(Boolean).join(' ').trim() ||
        (username && !username.includes('user_') && !username.includes('invited_') ? username : null) ||
        (email && !email.includes('@placeholder.com') ? email.split('@')[0] : null) ||
        null;
      if (displayName) {
        if (userId) participantDisplayNamesById[userId] = displayName;
        if (email) {
          participantDisplayNamesById[email] = displayName;
          participantDisplayNamesById[email.toLowerCase().trim()] = displayName;
        }
      }
      if (email) {
        const normalizedEmail = email.toLowerCase().trim();
        if (avatarStr) { builtAvatars[email] = avatarStr; builtAvatars[normalizedEmail] = avatarStr; }
        builtAvatarIds[email] = normalizeDisplayAvatarId(avatarIdStr);
        builtAvatarIds[normalizedEmail] = normalizeDisplayAvatarId(avatarIdStr);
      }
    });
    if (Object.keys(participantDisplayNamesById).length === 0 && challenge.participantDisplayNamesById) {
      participantDisplayNamesById = { ...challenge.participantDisplayNamesById };
    }
    if (Object.keys(builtAvatars).length > 0 || Object.keys(participantAvatarsById).length === 0) {
      participantAvatarsById = { ...participantAvatarsById, ...builtAvatars };
    }
    if (Object.keys(builtAvatarIds).length > 0 || Object.keys(participantAvatarIdsById).length === 0) {
      participantAvatarIdsById = { ...participantAvatarIdsById, ...builtAvatarIds };
    }
    if (Object.keys(builtAvatarConfigs).length > 0 || Object.keys(participantAvatarConfigsById).length === 0) {
      participantAvatarConfigsById = { ...participantAvatarConfigsById, ...builtAvatarConfigs };
    }
  }

  if (participantIds.length === 0 && challenge.participants) {
    const acceptedIds = challenge.participants
      .filter((p: RawParticipant) => {
        const pId = p.userId || p.user?.id;
        const statusFromList = challenge.participantStatuses?.find((s) => s.userId === pId)?.status;
        return (p.status ?? statusFromList)?.toUpperCase?.() === 'ACCEPTED';
      })
      .map((p: RawParticipant) => p.userId || p.user?.id)
      .filter((id): id is string => typeof id === 'string');
    participantIds.push(...acceptedIds);
  }

  return {
    id: challenge.id,
    creatorId: challenge.creatorId,
    name: challenge.name,
    description: challenge.description,
    oddsSnapshot: challenge.oddsSnapshot ?? null,
    stakeCoins: typeof challenge.stakeCoins === 'number' ? challenge.stakeCoins : 0,
    status: ((challenge.status ?? 'active').toLowerCase()) as ChallengeWithStats['status'],
    createdAt: new Date(challenge.createdAt),
    updatedAt: new Date(challenge.updatedAt),
    completedAt: challenge.completedAt ? String(challenge.completedAt) : null,
    isOpen: challenge.isOpen ?? false,
    takenAt: challenge.takenAt ?? null,
    participantIds,
    groupId: challenge.groupId ?? undefined,
    groupName: challenge.groupName ?? undefined,
    matchIds: challenge.matchIds || [],
    // Sent only when the viewer's picks are sealed (an open challenge they have not taken on).
    // This mapper rebuilds the challenge field-by-field, so a field it does not name is dropped —
    // and dropping this one leaves the take-on picks modal with nothing to ask (specs/open-challenges AC-7a).
    ...(challenge.requiredMarkets ? { requiredMarkets: challenge.requiredMarkets } : {}),
    scoringRules: {
      exactScore: challenge.exactScorePoints ?? 0,
      correctOutcome: challenge.correctOutcomePoints ?? 0,
    },
    league: challenge.league,
    dateRange: challenge.startDate && challenge.endDate
      ? { start: new Date(challenge.startDate), end: new Date(challenge.endDate) }
      : undefined,
    picks: Array.isArray(challenge.picks)
      ? challenge.picks.map((p: RawPick) => ({
        id: p.id,
        challengeId: p.challengeId || challenge.id,
        userId: p.userId || p.user?.id || '',
        matchId: p.matchId || p.match?.id || '',
        marketId: Number(p.marketId),
        marketName: p.marketName || '',
        value: p.value || '',
        odd: Number(p.odd),
        pointsEarned: p.pointsEarned,
        isCorrectOutcome: p.isCorrectOutcome,
        isExactScore: p.isExactScore,
        match: p.match,
        user: p.user,
      }))
      : [],
    participantCount: challenge.participantCount || participantIds.length,
    matchCount: challenge.matchCount || challenge.matchIds?.length || 0,
    leader: challenge.leader,
    myRank: challenge.myRank,
    myPoints: challenge.myPoints,
    participantStatuses: Array.isArray(challenge.participantStatuses)
      ? challenge.participantStatuses.map((p: { userId: string; status: string }) => ({
        userId: p.userId,
        status: (p.status?.toUpperCase?.() ?? p.status) as 'PENDING' | 'ACCEPTED' | 'DECLINED',
      }))
      : [],
    participantEmailsById,
    participantDisplayNamesById,
    participantAvatarsById,
    participantAvatarIdsById,
    participantAvatarConfigsById,
  } as ChallengeWithStats & {
    participantEmailsById: Record<string, string>;
    participantDisplayNamesById: Record<string, string>;
    participantAvatarsById: Record<string, string>;
    participantAvatarIdsById: Record<string, string>;
    participantAvatarConfigsById: Record<string, EquippedMap | null>;
  };
}
